// Automatic click capture (PRD §3.1). A SINGLE delegated listener on the document. Two
// tiers, in priority order:
//
//   1. EXPLICIT — an element opted in with `data-track="my_event"`. Emits that exact event,
//      with every `data-track-*` attribute as a prop. Use this for rich, named events.
//        <Button data-track="export_excel" data-track-source="stock">
//
//   2. AUTO — any interactive element (button / [role=button] / <a> / MUI button base)
//      emits a generic `ui_click` with a label derived from aria-label / title / text /
//      icon. This is what gives broad button coverage across the app WITHOUT touching the
//      114 call-sites. Non-interactive DOM (divs, text) is ignored → bounded noise, no
//      firehose.

const INTERACTIVE = "[data-track], button, a, [role='button'], [role='tab'], .MuiButtonBase-root"
const MAX_LABEL = 80

let started = false

function explicitProps(el) {
    const props = {}
    for (const { name, value } of Array.from(el.attributes)) {
        if (name.startsWith("data-track-")) {
            const key = name.slice("data-track-".length).replace(/-([a-z])/g, (_, c) => c.toUpperCase())
            props[key] = value
        }
    }
    return props
}

function clean(text) {
    return (text || "").replace(/\s+/g, " ").trim().slice(0, MAX_LABEL) || null
}

// Best-effort human label for an auto-captured control.
function deriveLabel(el) {
    const aria = el.getAttribute("aria-label") || el.getAttribute("title")
    if (aria) return clean(aria)
    const text = clean(el.textContent)
    if (text) return text

    // Icon-only control: name it from the icon so it isn't an empty label.
    const svg = el.querySelector?.("svg[data-icon]")          // FontAwesome SVG core
    if (svg) return `icon:${svg.getAttribute("data-icon")}`
    const fa = el.querySelector?.("[class*='fa-']")            // FA webfont (<i class="fa-...">)
    if (fa) {
        const m = (fa.getAttribute("class") || "").match(/fa-([a-z0-9-]+)/i)
        if (m) return `icon:${m[1]}`
    }
    const svgTitle = clean(el.querySelector?.("svg title")?.textContent)
    if (svgTitle) return svgTitle

    // Last resort: a test id, if the app sets one.
    const testid = el.getAttribute("data-testid")
    if (testid) return clean(testid)
    return null
}

function onClick(track, e) {
    try {
        const el = e.target?.closest?.(INTERACTIVE)
        if (!el) return

        // Tier 1 — explicit data-track wins.
        const explicit = el.closest("[data-track]")
        if (explicit) {
            const name = explicit.getAttribute("data-track")
            if (name) track(name, explicitProps(explicit))
            return
        }

        // Tier 2 — generic auto-capture for interactive controls.
        const label = deriveLabel(el)
        const role = el.getAttribute("role")
        track("ui_click", {
            label,
            tag: el.tagName?.toLowerCase(),
            role: role || undefined,
            href: el.tagName === "A" ? el.getAttribute("href") : undefined,
        })
    } catch { /* fail-silent — never disturb the real click handler */ }
}

export function startClickCapture(track) {
    if (started || typeof document === "undefined") return
    started = true
    document.addEventListener("click", e => onClick(track, e), { capture: true, passive: true })
}
